import React, { useContext, useState } from "react";
import BPOHero from "assets/generated_images/bpo.png";
import Winr from "assets/winr.png";
import Datapharm from "assets/Datapharm_Logo1.png";
import SectionWrapper from "../ui/SectionWrapper";
import { OutletContext } from "../../layouts/DefaultLayout";
import { Link } from "react-router-dom";
import pharmacist from "assets/generated_images/pharmacist.png";
import aideveloper from "assets/generated_images/ai-programmers.png";
import webdeveloper from "assets/generated_images/web-developer.png";
import QualityAssuranceImg from "assets/generated_images/quality-asurance.png";
import DataAdministratorImg from "assets/generated_images/database-administrator.png";

type Role = {
  id: string;
  title: string;
  description: string;
  image: string;
};

const roles: Role[] = [
  {
    id: "ai",
    title: "AI Programmers",
    description:
      "Highly skilled AI programmers specialising in automation, data processing, machine learning, and enterprise-grade systems.",
    image: aideveloper,
  },
  {
    id: "web",
    title: "Web Developers",
    description:
      "Web developers available for large and small projects with considerable knowledge and expertise in UK consumer data.",
    image: webdeveloper,
  },
  {
    id: "qa",
    title: "Quality Assurance",
    description:
      "Quality assurance specialists providing manual and automated testing to ensure accuracy, compliance, and operational reliability.",
    image: QualityAssuranceImg,
  },
  {
    id: "db",
    title: "Database Administrators",
    description:
      "Database administrators experienced in secure data storage, performance optimisation, and regulatory compliance.",
    image: DataAdministratorImg,
  },
  {
    id: "pharma",
    title: "Pharmacists",
    description:
      "Qualified pharmacists supporting pharmaceutical operations and regulatory-driven workflows.",
    image: pharmacist,
  },
];

const BPO: React.FC = () => {
  const [openId, setOpenId] = useState<string | null>(null);
  const { setConsultOpen } = useContext(OutletContext);

  return (
    <SectionWrapper>
      <div className="w-full rounded-b-lg bg-[#F2F4F7] overflow-hidden">

        {/* MOBILE → COLUMN | DESKTOP → ROW */}
        <div className="flex flex-col lg:flex-row">

          {/* IMAGE (TOP ON MOBILE, RIGHT ON DESKTOP) */}
          <div className="flex justify-center lg:justify-end order-1 lg:order-2">
            <div className="relative w-full h-64 sm:h-80 lg:w-[300px] lg:h-[620px] overflow-hidden">
              <img
                src={BPOHero}
                alt="BPO sector specialists"
                className="h-full w-full object-cover object-center"
                width={300}
                height={620}
                loading="lazy"
                decoding="async"
              />
              <div className="absolute inset-0 bg-[#0B2545]/5" />
            </div>
          </div>

          {/* LEFT CONTENT */}
          <div className="flex-1 px-8 pb-6 order-2 lg:order-1">
            <p className="mt-8 sm:mt-6 max-w-xl text-[15px] leading-relaxed text-[#4B5C73]">
              There are tens of thousands of BPO companies in various countries throughout the world.
            </p>

            <div className="mt-8">
              <button
                className="inline-flex rounded-md bg-[#0B2545] px-6 py-3 text-sm font-medium text-white hover:bg-[#091D35] transition"
                onClick={() => setConsultOpen(true)}
              >
                Speak to our BPO team
              </button>
            </div>

            <div className="mt-6 space-y-5 text-[15px] leading-relaxed text-[#4B5C73]">
              <p>
                Philippines is globally recognised as one of the world’s leading locations in the BPO sector.
                Reasons for this are many, English both written and oral, high volumes of college and university
                graduates many of these focusing on IT.
              </p>

              <p>
                When choosing a BPO provider as partner the focus should be on the sector they specialize,
                a one solution fits all mentality is clumsy and unsatisfactory.
              </p>

              <div className="rounded-md border-l-4 border-blue-600 bg-white px-5 py-4 shadow-sm">
                <p className="font-medium text-[#0B2545]">
                  We exclusively support organisations operating in:
                </p>
                <p className="mt-1">
                  <strong>Identity Verification (IDV)</strong>,{" "}
                  <strong>Know Your Customer (KYC)</strong>, and the{" "}
                  <strong>Pharmaceutical sectors</strong>.
                </p>
              </div>

              <p>
                Our teams of BPO personnel are dedicated to their respective client’s and understand the
                client’s business. By having sector specific specialists our staff won’t just do as instructed
                but, where applicable, assist with improving and provide solutions.
              </p>
            </div>
          </div>
        </div>

        {/* ROLES */}
        <div className="px-8 shadow-sm">
          <div className="my-10 h-px bg-gray-300" />

          <h3 className="text-lg font-semibold text-[#0B2545]">
            Our BPO personnel include
          </h3>

          <ul className="mt-6 space-y-4">
            {roles.map((role) => {
              const isOpen = openId === role.id;

              return (
                <li
                  key={role.id}
                  className={`rounded-lg border bg-white overflow-hidden transition ${
                    isOpen ? "border-blue-300 shadow-sm" : "border-gray-200"
                  }`}
                >
                  <button
                    onClick={() => setOpenId(isOpen ? null : role.id)}
                    className="flex w-full items-center justify-between px-5 py-4 text-left font-medium text-[#0B2545] hover:bg-[#F9FAFB] transition"
                  >
                    {role.title}
                    <span className="text-blue-600 text-lg">
                      {isOpen ? "−" : "+"}
                    </span>
                  </button>

                  {isOpen && (
                    <div className="border-t border-gray-200 bg-[#F9FAFB] px-6 py-7">
                      <div className="grid gap-6 md:grid-cols-[1fr_2fr] items-start">
                        <div className="rounded-lg bg-white/70 backdrop-blur-sm p-2 shadow-sm">
                          <div className="relative h-40 w-full overflow-hidden rounded-md">
                            <img
                              src={role.image}
                              alt={role.title}
                              className="absolute inset-0 h-full w-full object-cover"
                              loading="lazy"
                              decoding="async"
                            />
                            <div className="absolute inset-0 bg-[#0B2545]/5" />
                          </div>
                        </div>

                        <div className="space-y-4 text-[15px] text-[#4B5C73]">
                          <p>{role.description}</p>
                          <button className="inline-flex rounded-md bg-[#0B2545] px-5 py-2 text-sm font-medium text-white hover:bg-[#091D35] transition">
                            Enquire about this role
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>

          {/* ORGANISATIONS */}
          <h3 className="text-lg font-semibold text-[#0B2545] py-6">
            Companies we support
          </h3>

          <div className="grid grid-cols-2 sm:grid-cols-3 gap-48 items-center max-w-md pb-10">
            <Link
              to=""
              className="flex items-center justify-center h-32 w-40"
            >
              <img
                src={Datapharm}
                alt="Datapharm"
                className="max-h-full max-w-full object-contain"
              />
            </Link>

            <Link
              to=""
              className="flex items-center justify-center h-32 w-40"
            >
              <img
                src={Winr}
                alt="Winr"
                className="max-h-full max-w-full object-contain"
              />
            </Link>
          </div>
        </div>
      </div>
    </SectionWrapper>
  );
};

export default BPO;
